import Vue from "vue";
import VueRouter from "vue-router";
import {
  guardRoute,
  guardLoginRoute,
  setHeadTitle,
  getRouterMetaTitle,
  guardShopifyLoginRoute
} from "@/helpers/router";
import { changeGeneralLocalStorageVariables, secureInput } from "@/helpers/common";
import { isOnboardingPage } from "@/helpers/onboarding";
import { sendGoogleTagManagerEvent } from "@/helpers/main";
import store from "@/store";
import { clientRoles, localStorageKeys } from "@/enums/common";

Vue.use(VueRouter);


const LogIn = () => import ( /* webpackChunkName: "Login" */ "@/views/auth/Login.vue");
const AppsumoLogin = () => import ( /* webpackChunkName: "AppsumuLogin" */ "@/views/auth/AppsumoLogin.vue");
const ShopifyLogin = () => import ( /* webpackChunkName: "ShopifyLogin" */ "@/views/auth/ShopifyLogin.vue");
const SignUp = () => import ( /* webpackChunkName: "SignUp" */ "@/views/auth/SignUp.vue");
const Logout = () => import(/* webpackChunkName: "Logout" */ "@/views/auth/Logout");
const CustomizeInsight = () => import ( /* webpackChunkName: "CustomizeInsight" */ "@/views/onboarding/CustomizeInsight.vue");
const InsightCategories = () => import ( /* webpackChunkName: "InsightCategories" */ "@/views/onboarding/InsightCategories.vue");
const Connect = () => import ( /* webpackChunkName: "OnboardingConnect" */ "@/views/onboarding/Connect.vue");
const OnboardingPaymentSubscription = () => import(/* webpackChunkName: "OnboardingPaymentSubscription" */ "@/views/onboarding/payment/Subscription");
const Dashboard = () => import(/* webpackChunkName: "Dashboard" */ "@/views/dashboard/Dashboard");
const ExploreNewInsight = () => import ( /* webpackChunkName: "ExploreNewInsight" */ "@/views/explorenewinsight/ExploreNewInsight.vue");
const MyInsights = () => import(/* webpackChunkName: "MyInsights" */ "@/views/my_insights/MyInsights");
const PackageResult = () => import ( /* webpackChunkName: "PackageResult" */ "@/views/packageresult/PackageResult.vue");
const KPIManagement = () => import ( /* webpackChunkName: "KpiManagement" */ "@/views/kpis/KPIManagement.vue");
const KPIDetail = () => import ( /* webpackChunkName: "KPIDetail" */ "@/views/kpis/KPIDetail.vue");
const CreateNewCollection = () => import ( /* webpackChunkName: "CreateNewCollection" */ "@/views/collection/CreateNewCollection");
const PublicInsightPost = () => import ( /* webpackChunkName: "PublicInsightPost" */ "@/views/publicinsightpost/PublicInsightPost.vue");
const UserManagement = () => import ( /* webpackChunkName: "UserManagement" */ "@/views/usermanagement/UserManagement.vue");
const Notifications = () => import ( /* webpackChunkName: "Notifications" */ "@/views/notifications/Notifications.vue");
const ConnectYourApp = () => import ( /* webpackChunkName: "ConnectYourApp" */ "@/views/connect-your-app/ConnectYourApp.vue")
const PaymentSubscription = () => import( /* webpackChunkName: "PaymentSubscription" */ "@/views/payment/Subscription");
const PaymentProPlan = () => import( /* webpackChunkName: "PaymentProPlan" */ "@/views/payment/ProPlan");
const PaymentSuccess = () => import( /* webpackChunkName: "PaymentSuccess" */ "@/views/payment/Success");
const PaymentCancel = () => import(/* webpackChunkName: "PaymentCancel" */ "@/views/payment/Cancel");
const ExplorePackages = () => import(/* webpackChunkName: "ExplorePackages" */ "@/views/ExplorePackages");
const Maintenance = () => import(/* webpackChunkName: "Maintenance" */ "@/views/maintenance_mode/Maintenance");


const router = new VueRouter({
  routes: [
    /* Start - Old URL to New URL */
    {
      path: "/signup",
      redirect: { name: "signup" }
    },
    {
      path: "/login",
      redirect: { name: "login" }
    },
    {
      path: "/appsumo-login",
      redirect: { name: "appsumoLogin" }
    },
    {
      path: "/shopify-login",
      redirect: { name: "shopifyLogin" }
    },
    {
      path: "/logout",
      redirect: { name: "logout" }
    },
    {
      path: "/customize",
      redirect: { name: "customize" }
    },
    {
      path: "/insight-categories",
      redirect: { name: "insight-categories" }
    },
    {
      path: "/connect",
      redirect: { name: "connect" }
    },
    /* End -  Old URL to New URL */
    {
      name: "login",
      path: "/auth/login",
      component: LogIn,
      beforeEnter: guardLoginRoute,
      meta: {
        title: getRouterMetaTitle('Login'),
      },
    },
    {
      name: "appsumoLogin",
      path: "/auth/appsumo/login",
      component: AppsumoLogin,
      beforeEnter: guardLoginRoute,
      meta: {
        title: getRouterMetaTitle('Appsumo Login Screen'),
      },
    },
    {
      name: "shopifyLogin",
      path: "/auth/shopify/login",
      component: ShopifyLogin,
      beforeEnter: guardShopifyLoginRoute,
      meta: {
        title: getRouterMetaTitle('Shopify Login Screen'),
      },
    },
    {
      name: "signup",
      path: "/auth/signup",
      component: SignUp,
      beforeEnter: guardLoginRoute,
      meta: {
        title: getRouterMetaTitle('Signup'),
      },
    },
    {
      name: "logout",
      path: "/auth/logout",
      component: Logout,
      meta: {
        title: getRouterMetaTitle('Logout'),
      },
    },
    {
      name: "customize",
      path: "/onboarding/customize",
      component: CustomizeInsight,
      beforeEnter: guardRoute,
      meta: {
        title: getRouterMetaTitle('Customize Insight'),
      }
    },
    {
      name: "insight-categories",
      path: "/onboarding/insight-categories",
      component: InsightCategories,
      beforeEnter: guardRoute,
      meta: {
        title: getRouterMetaTitle('Insight Categories'),
      }
    },
    {
      name: "connect",
      path: "/onboarding/connect",
      component: Connect,
      beforeEnter: guardRoute,
      meta: {
        title: getRouterMetaTitle('Connect'),
      }
    },
    {
      path: "/onboarding/payment/subscription",
      component: OnboardingPaymentSubscription,
      name: "onboardingPaymentSubscription",
      beforeEnter: guardRoute,
      meta: {
        title: getRouterMetaTitle('Onboarding Payment Subscription'),
      }
    },
    {
      name: "dashboard",
      path: "/",
      component: Dashboard,
      beforeEnter: guardRoute,
      meta: {
        title: getRouterMetaTitle('Dashboard')
      }
    },
    {
      name: "explore",
      path: "/explore-insight",
      component: ExploreNewInsight,
      beforeEnter: guardRoute,
      meta: {
        title: getRouterMetaTitle('Explore New Insights'),
      }
    },
    {
      name: "my-insights",
      path: "/my-insights",
      component: MyInsights,
      beforeEnter: guardRoute,
      meta: {
        title: getRouterMetaTitle('My Insights'),
      }
    },
    {
      name: "package-result",
      path: "/package-result",
      component: PackageResult,
      beforeEnter: guardRoute,
      meta: {
        title: route => getRouterMetaTitle('Package Result', route.params.pageTitle),
      }
    },
    {
      name: "kpi-management",
      path: "/kpi-management",
      component: KPIManagement,
      beforeEnter: guardRoute,
      meta: {
        title: getRouterMetaTitle('KPI Management'),
      }
    },
    {
      name: "kpi-detail",
      path: "/kpi/:packageID/:taskID",
      component: KPIDetail,
      props: true,
      beforeEnter: guardRoute,
      meta: {
        title: route => getRouterMetaTitle('KPI Detail', secureInput(route.query.kpi)),
      }
    },
    {
      name: "create-new-collection",
      path: "/create-new-collection",
      component: CreateNewCollection,
      beforeEnter: guardRoute,
      meta: {
        title: getRouterMetaTitle('Add New Data Source'),
      }
    },
    {
      name: "public-insight-post",
      path: "/public-insight-post/:id",
      component: PublicInsightPost,
      meta: {
        title: getRouterMetaTitle('Public Insight Post'),
      }
    },
    {
      name: "profile",
      path: "/account",
      component: UserManagement,
      beforeEnter: guardRoute,
      meta: {
        title: getRouterMetaTitle('User Management'),
      }
    },
    {
      name: "notifications",
      path: "/notifications",
      component: Notifications,
      beforeEnter: guardRoute,
      meta: {
        title: getRouterMetaTitle('Notifications'),
      }
    },
    {
      name: "connection",
      path: "/connect-your-app",
      component: ConnectYourApp,
      beforeEnter: guardRoute,
      meta: {
        title: getRouterMetaTitle('Connection Your App'),
      }
    },
    {
      name: "paymentSubscription",
      path: "/payment/subscription",
      component: PaymentSubscription,
      beforeEnter: guardRoute,
      meta: {
        title: getRouterMetaTitle('Payment Subscription'),
      }
    },
    {
      name: "paymentProplan",
      path: "/payment/proplan",
      component: PaymentProPlan,
      beforeEnter: guardRoute,
      meta: {
        title: getRouterMetaTitle('Payment Pro Plan'),
      }
    },
    {
      name: "paymentSuccess",
      path: "/payment/success",
      component: PaymentSuccess,
      meta: {
        title: getRouterMetaTitle('Payment Success'),
      }
    },
    {
      name: "paymentCancel",
      path: "/payment/cancel",
      component: PaymentCancel,
      beforeEnter: guardRoute,
      meta: {
        title: getRouterMetaTitle('Payment Cancel'),
      }
    },
    {
      name: "explore-packages",
      path: "/explore-packages",
      component: ExplorePackages,
      meta: {
        title: getRouterMetaTitle('Explore Packages'),
      }
    },
    {
      name: 'maintenance',
      path: "/maintenance",
      alias: '*',
      component: Maintenance,
      meta: {
        title: getRouterMetaTitle('We are in maintenance'),
      }
    },
  ],
  mode: "history"
});


router.beforeEach((to, from, next) => {

  // return to.name == "maintenance" ? next() : next({ name: "maintenance" });


  if (store.getters['common/getActiveUserRole'] == clientRoles.viewer) {

    const unauthorizedRoutes = [ "create-new-collection", "profile" ];

    if (unauthorizedRoutes.includes(to.name)) {
      next({ name: 'dashboard' });
      return;
    }

  }

  next();

})

router.afterEach(to=> {

  sendGoogleTagManagerEvent();

  if (isOnboardingPage(to.name)) {

    changeGeneralLocalStorageVariables({
      key: localStorageKeys.lastVisitedPage,
      value: to.fullPath
    });

  }

});


setHeadTitle(router);

export default router



// WEBPACK FOOTER //
// ./src/router/index.js